import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type CommentEntityKind, commentKeys } from '@/entities/comment'
import { apiRequest } from '@/shared/api'

function useInvalidate(entityKind: CommentEntityKind, entityId: string) {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: commentKeys.byEntity(entityKind, entityId) })
}

export function useAddComment(entityKind: CommentEntityKind, entityId: string) {
  const invalidate = useInvalidate(entityKind, entityId)
  return useMutation({
    mutationFn: (vars: { text: string; parent_id?: string; internal?: boolean }) =>
      apiRequest({
        method: 'POST',
        url: '/comments',
        body: {
          entity_kind: entityKind,
          entity_id: entityId,
          text: vars.text,
          ...(vars.parent_id ? { parent_id: vars.parent_id } : {}),
          ...(vars.internal ? { internal: true } : {}),
        },
      }),
    onSuccess: () => void invalidate(),
  })
}

export function useEditComment(entityKind: CommentEntityKind, entityId: string) {
  const invalidate = useInvalidate(entityKind, entityId)
  return useMutation({
    mutationFn: (vars: { commentId: string; text: string }) =>
      apiRequest({
        method: 'PATCH',
        url: `/comments/${vars.commentId}`,
        body: { text: vars.text },
      }),
    onSuccess: () => void invalidate(),
  })
}

export function useResolveComment(entityKind: CommentEntityKind, entityId: string) {
  const invalidate = useInvalidate(entityKind, entityId)
  return useMutation({
    mutationFn: (vars: { commentId: string; resolved: boolean }) =>
      apiRequest({
        method: 'POST',
        url: `/comments/${vars.commentId}/resolve`,
        body: { resolved: vars.resolved },
      }),
    onSuccess: () => void invalidate(),
  })
}

export function useDeleteComment(entityKind: CommentEntityKind, entityId: string) {
  const invalidate = useInvalidate(entityKind, entityId)
  return useMutation({
    mutationFn: (commentId: string) =>
      apiRequest({ method: 'DELETE', url: `/comments/${commentId}` }),
    onSuccess: () => void invalidate(),
  })
}
